// Storage sizing engine — PRODUCT-51.
// Cardinal rule: growth buffer and RAID overhead are applied once, sequentially.
// DR storage: SAN and NAS always fully replicated; local disk only for Hot DR.

import type { VMConfig, SizingConfig, StorageResult } from '../../types';

function vmAppliesToSite(vm: VMConfig, site: 'primary' | 'dr'): boolean {
  return vm.siteAssignment === site || vm.siteAssignment === 'both';
}

/**
 * Run the storage sizing engine for a given site.
 * Returns one StorageResult covering all environments at the site.
 * (Storage is not split per environment — capacity is shared across the cluster.)
 */
export function runStorageSizing(
  vms: VMConfig[],
  config: SizingConfig,
  site: 'primary' | 'dr',
): StorageResult {
  const notes: string[] = [];

  const siteVms = vms.filter(vm => vmAppliesToSite(vm, site));

  // ── Step 1: Sum usable storage per type ─────────────────────────────────────

  // For DR site: SAN and NAS are always fully replicated (100%) regardless of drTier.
  // Local disk is replicated only for Hot DR.
  const includeLocalAtDr = site === 'dr' && config.drTier === 'hot';

  const usableSanGb = siteVms.reduce((sum, vm) => sum + vm.diskSanGb * vm.quantity, 0);
  const usableNasGb = siteVms.reduce((sum, vm) => sum + vm.diskNasGb * vm.quantity, 0);
  const rawLocalGbPerVm = siteVms.reduce((sum, vm) => sum + vm.diskLocalGb * vm.quantity, 0);

  // For primary: include all local disk.
  // For DR non-Hot: local disk is not replicated — zero it out.
  const usableLocalGb = site === 'primary' || includeLocalAtDr ? rawLocalGbPerVm : 0;

  const totalVmCount = siteVms.reduce((sum, vm) => sum + vm.quantity, 0);

  notes.push(
    `Storage inventory (${totalVmCount} VM instance(s) at ${site} site): ` +
    `SAN ${usableSanGb} GB usable, NAS ${usableNasGb} GB usable, ` +
    `local ${usableLocalGb} GB usable`,
  );

  if (site === 'dr') {
    notes.push(
      `DR replication policy: SAN and NAS always fully replicated (DR tier: ${config.drTier}). ` +
      `Local disk replicated: ${includeLocalAtDr ? 'yes (Hot DR)' : 'no (Cold/Warm DR — local disk not replicated)'}`,
    );
  }

  // ── Step 2: Apply growth buffer ──────────────────────────────────────────────

  const growthFactor = 1 + config.storageGrowthBufferPct;

  const usableSanWithGrowth = usableSanGb * growthFactor;
  const usableNasWithGrowth = usableNasGb * growthFactor;
  const usableLocalWithGrowth = usableLocalGb * growthFactor;

  notes.push(
    `Growth buffer applied (×${growthFactor}): ` +
    `SAN ${usableSanGb} → ${usableSanWithGrowth} GB, ` +
    `NAS ${usableNasGb} → ${usableNasWithGrowth} GB, ` +
    `local ${usableLocalGb} → ${usableLocalWithGrowth} GB`,
  );

  // ── Step 3: Apply RAID / usable-to-raw overhead ──────────────────────────────

  const rawSanGb = usableSanWithGrowth * config.storageUsableToRawRatio;
  const rawNasGb = usableNasWithGrowth * config.storageUsableToRawRatio;
  const rawLocalGb = usableLocalWithGrowth * config.storageUsableToRawRatio;

  notes.push(
    `Usable-to-raw ratio applied (×${config.storageUsableToRawRatio}): ` +
    `SAN ${usableSanWithGrowth} → ${rawSanGb} GB raw, ` +
    `NAS ${usableNasWithGrowth} → ${rawNasGb} GB raw, ` +
    `local ${usableLocalWithGrowth} → ${rawLocalGb} GB raw`,
  );

  notes.push(
    `Storage architecture: ${config.storageArchitecture}`,
  );

  // ── Step 4: IOPS aggregation ─────────────────────────────────────────────────

  const sanVms = siteVms.filter(vm => vm.diskSanGb > 0 || vm.diskSanIops > 0);
  const totalSanIops = siteVms.reduce((sum, vm) => sum + vm.diskSanIops * vm.quantity, 0);

  notes.push(`Total SAN IOPS (sum across all VMs): ${totalSanIops}`);

  // ── Step 5: Effective IOPS — IOPS-weighted average read/write ratio ──────────

  let effectiveIopsRequired = 0;

  if (totalSanIops > 0 && sanVms.length > 0) {
    // Compute IOPS-weighted average read ratio across all VMs
    const weightedReadRatioSum = siteVms.reduce(
      (sum, vm) => sum + (vm.diskReadWriteRatio / 100) * vm.diskSanIops * vm.quantity,
      0,
    );
    const avgReadRatio = weightedReadRatioSum / totalSanIops;
    const avgWriteRatio = 1 - avgReadRatio;

    // effectiveIopsRequired = totalSanIops / (readRatio + writeRatio × raidWritePenaltyFactor)
    const denominator = avgReadRatio + avgWriteRatio * config.raidWritePenaltyFactor;
    effectiveIopsRequired = totalSanIops / denominator;

    notes.push(
      `Effective IOPS: ${totalSanIops} ÷ ` +
      `(${avgReadRatio.toFixed(3)} read + ${avgWriteRatio.toFixed(3)} write × ${config.raidWritePenaltyFactor} RAID penalty) ` +
      `= ${effectiveIopsRequired.toFixed(0)} effective IOPS required from storage array`,
    );
  } else {
    notes.push(`No SAN IOPS to calculate (no SAN-attached VMs at this site).`);
  }

  return {
    site,
    usableSanGb,
    usableNasGb,
    usableLocalGb,
    rawSanGb,
    rawNasGb,
    rawLocalGb,
    totalSanIops,
    effectiveIopsRequired,
    notes,
  };
}
